import { useMutation } from '@tanstack/react-query';
import { Editor } from '@tiptap/react';
import { dataExtractorAPI, DataExtractorFormVariable } from 'api/data-extractor';
import clsx from 'clsx';
import {
  AvoAccordian,
  AvoAccordianPanel,
  AvoAccordianTrigger,
} from 'components/utils/AvoAccordian';
import { AvoSwitch } from 'components/utils/AvoSwitch';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/utils/AvoTooltip';
import { ComboBox } from 'components/utils/ComboBox';
import Input from 'components/utils/Input';
import Label from 'components/utils/Label';
import { AvoSlideCloseModal } from 'components/utils/panels/AvoSlideCloseModal';
import { Header } from 'components/utils/panels/Header';
import RequiredMark from 'components/utils/requiredMark';
import { StaffOnlyBadgeType, StaffOnlyWrapper } from 'components/utils/StaffOnlyWrapper';
import {
  RichTextMenuButtonTypes,
  SuggestionTypeEnum,
} from 'components/utils/tiptap/tiptapInterfaces';
import { CustomToast } from 'components/utils/toast-message';
import { Body2, Caption2 } from 'components/utils/typo';
import VariableAutoGenerator from 'components/utils/variable-generator';
import { CARD_DATA_EXTRACTOR } from 'constants/variables';
import { ChevronDown, Info } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { getVariableName } from 'utils/gptUtils';
import { useTriggers } from '../../../hooks/module/resources/useTriggers';
import { useDataExtractorFormContext } from '../../module/cards/DataExtractorCard';
import SituationBuilder from '../../resources/triggers/SituationBuilder';
import { FormTooltip } from '../../utils/helpInfo/FormTooltip';
import { MentionProvider } from '../../utils/module/MentionContext';
import { Footer } from '../../utils/panels/Footer';
import { Tiptap } from '../../utils/tiptap/Tiptap';
import { AIModelSelect } from '../utils/AIModelSelect';
import { DataToConsumeListInput } from './data-to-consume/DataToConsumeListInput';
import { VariablesToGenerateController } from './variable-to-generate/VariableToGenerateController';
import { SlidePanel } from '../../utils/panels/AvoSlidePanel';
import { AIModelProvider } from '../../../api/ai';

interface DataExtractorFormProps {
  modal: boolean;
  onSave: () => void;
  closeModal: (type?: any) => void;
  closePanel: (command?: string) => void;
}

const richTextButtonsShowList: RichTextMenuButtonTypes[] = [
  'textStyles',
  'bold',
  'italic',
  'highlight',
  'bulletList',
  'orderedList',
  'more',
];

export const DataExtractorForm = ({
  modal,
  onSave,
  closeModal,
  closePanel,
}: DataExtractorFormProps) => {
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  // for trigger panel
  const [triggerPanelOpen, setTriggerPanelOpen] = useState(false);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [triggerId, setTriggerId] = useState<number | undefined>();
  const triggerComponentProps = {
    toggleModal: () => setTriggerPanelOpen(false),
    selectTrigger: (value) => setTrigger(value),
    createModal: true,
    modal: triggerModalOpen,
    triggerId: triggerId,
    savePosition: () => {},
    toogleModalPane: () => setTriggerModalOpen(false),
  };

  const [isMutating, setIsMutating] = useState(false);

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    setError,
    formState: { errors, isDirty },
  } = useDataExtractorFormContext();
  const title = watch('title');
  const description = watch('description');

  const { mutateAsync } = useMutation({ mutationFn: dataExtractorAPI.upsertDataExtractor });

  const onKeyDown = (e: any) => {
    if (e.key === 'Enter' && e.target.nodeName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const setVariableName = (variable: string) => {
    setValue('variable_name', variable, { shouldDirty: true });
  };

  const setTrigger = (value) => {
    setValue('trigger', value, { shouldDirty: true });
  };

  const { data: triggers } = useTriggers();
  const triggerOptions = triggers.map((trigger) => ({
    value: trigger.id,
    label: trigger.title,
    isEditingDisabled: trigger.candidate_trigger || trigger.is_global,
  }));
  const variablesToGenerate = watch('variables_to_generate');
  const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(
    Object.keys(description).length === 0
  );
  const isDisabled = !title || isDescriptionEmpty || variablesToGenerate.length < 1; //|| isInValidDataToConsume;

  const onSubmit = async (data: DataExtractorFormVariable) => {
    if (isMutating) return;
    else if (isDescriptionEmpty) {
      setError('description', { type: 'required', message: 'Description is required' });
      return;
    } else if (!data.data_to_consume.length) {
      setError('data_to_consume', { type: 'required', message: 'At least one data is required' });
      return;
    } else if (
      data.data_to_consume.some((item) => !item.label || Object.keys(item.content).length === 0)
    ) {
      setError('data_to_consume', {
        type: 'required',
        message: 'All data must have a label and content',
      });
      return;
    } else if (!data.variables_to_generate.length) {
      setError('variables_to_generate', {
        type: 'required',
        message: 'At least one variable is required',
      });
      return;
    }

    if (isDisabled) {
      toast.error(CustomToast, { data: 'Please fill out the title field.' });
    }

    setIsMutating(true);

    if (!data.variable_name) {
      data.variable_name = await getVariableName(title);
    }
    isDirty && (await mutateAsync(data));
    reset(data);
    onSave();
  };

  const closePanelHandler = () => {
    isDirty ? setCloseModalOpen(true) : closePanel();
  };

  return (
    <div className='h-full px-[16px]'>
      <Header
        className='sticky top-0 z-10 bg-white !px-0 !pr-3 pt-[20px]'
        title={CARD_DATA_EXTRACTOR}
        toggleModal={closePanelHandler}
      />
      <form
        autoComplete='off'
        onKeyDown={onKeyDown}
        onSubmit={handleSubmit(onSubmit)}
        className='flex h-full flex-col pb-0'
      >
        <div className='space-y-[5px]'>
          <div className='flex gap-1'>
            <Body2 className='text-gray-900'>Trigger</Body2>
            <RequiredMark />
          </div>
          <ComboBox
            onChange={setTrigger}
            options={triggerOptions}
            selectedValue={watch('trigger')}
            onCreate={() => {
              setTriggerId(undefined);
              setTriggerPanelOpen(true);
            }}
            onEdit={(id) => {
              setTriggerId(+id);
              setTriggerPanelOpen(true);
            }}
            createButtonLabel='Create New Trigger'
          />
        </div>

        <div className='!mt-8'>
          <div className='space-y-[4px]'>
            <div className='flex gap-1'>
              <Body2 className='text-gray-900'>Title</Body2>
              <RequiredMark />
            </div>
            <Input
              tabIndex={1}
              maxLength={200}
              placeholder='e.g. AI Clinical Document Analyzer'
              {...register('title', {
                maxLength: 200,
                required: { value: true, message: 'Title field is required' },
              })}
              className={clsx(
                errors.title &&
                  'border-red-500 ring-1 !ring-red-500' /* TODO: Need to remove ring after remove global style*/
              )}
            />
            {errors.title && <span className='text-error'>{errors.title.message}</span>}
            <div className='text-right text-[12px] text-gray-700'>{title.length}/200</div>
          </div>
        </div>

        <div className='!mt-3'>
          <div className='space-y-[5px]'>
            <div className='flex gap-1'>
              <Body2 className='text-gray-900'>Function description</Body2>
              <RequiredMark />
            </div>
            <div className={clsx({ 'rounded ring-1 ring-error': errors.description })}>
              <MentionProvider
                excludedSuggestionTypes={[SuggestionTypeEnum.DATA_EXTRACTOR]}
                performChoiceCodeSubstitution
              >
                <Tiptap
                  onUpdate={(editor: Editor) => {
                    setIsDescriptionEmpty(!editor.getText().trim().length);
                    setValue('description', editor.getJSON(), { shouldDirty: true });
                    clearErrors('description');
                  }}
                  initialContent={description as any}
                  placeholder={
                    'Write a prompt. i.e Extract valuable information from clinical documents presented here.'
                  }
                  richTextButtonsShowList={richTextButtonsShowList}
                  wrapperClassNames='flex-grow max-h-[600px] min-h-[280px] !h-auto'
                />
              </MentionProvider>
            </div>
            {errors.description && <span className='text-error'>{errors.description.message}</span>}
          </div>
        </div>

        <div className='mt-6'>
          <div className='space-y-[8px]'>
            <div className='flex items-center gap-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Data to consume</Body2>
                <RequiredMark />
              </div>
              <FormTooltip message='Data Extractor' />
            </div>
            <div className='space-y-1'>
              <div className={clsx({ 'rounded ring-1 ring-error': errors.data_to_consume })}>
                <DataToConsumeListInput />
              </div>
              {errors.data_to_consume && (
                <span className='text-error'>{errors.data_to_consume.message}</span>
              )}
            </div>
          </div>
        </div>

        <div className='mt-6'>
          <div className='space-y-2'>
            <div className='flex gap-1'>
              <Body2 className='text-gray-900'>Variables to generate</Body2>
              <RequiredMark />
            </div>
            <div
              className={clsx('space-y-2', {
                'rounded ring-1 ring-error': errors.variables_to_generate,
              })}
            >
              <VariablesToGenerateController />
            </div>
            {errors.variables_to_generate && (
              <span className='text-error'>{errors.variables_to_generate.message}</span>
            )}
          </div>
        </div>
        <div className='my-6'>
          <AvoAccordian>
            <AvoAccordianTrigger>
              {({ isOpen }) => (
                <div className='flex gap-1 py-2'>
                  <Body2 className='text-gray-900'>Advanced Settings</Body2>
                  <ChevronDown
                    size={20}
                    strokeWidth={1.5}
                    className={clsx(isOpen && 'rotate-180', 'text-gray-800 transition-all')}
                  />
                </div>
              )}
            </AvoAccordianTrigger>
            <AvoAccordianPanel>
              <div className='space-y-6 rounded border border-gray-300 p-4'>
                <div className='w-full space-y-1'>
                  <Body2>Model</Body2>
                  <AIModelSelect
                    providers={[AIModelProvider.OPENAI]}
                    model_id={watch('model_id')}
                    onChange={(model_id) => {
                      setValue('model_id', model_id, { shouldDirty: true });
                    }}
                  />
                </div>
                <StaffOnlyWrapper type={StaffOnlyBadgeType.BETA}>
                  <Label className='flex justify-between'>
                    <div className='flex items-center gap-1 text-gray-900'>
                      <Body2>Hide Output</Body2>
                      <Tooltip placement='bottom'>
                        <TooltipTrigger>
                          <Info size={20} color='#C5D1D8' />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className='w-[280px] gap-4 rounded bg-gray-800 px-3 py-2 shadow'>
                            <Caption2 className='text-white'>
                              Enable this option to hide the Data Extractor from view.
                            </Caption2>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <AvoSwitch
                      checked={watch('is_hide')}
                      onChange={(value) => setValue('is_hide', value, { shouldDirty: true })}
                    />
                  </Label>
                </StaffOnlyWrapper>
              </div>
            </AvoAccordianPanel>
          </AvoAccordian>
        </div>
        <VariableAutoGenerator
          question={title}
          tagTitle='Data Extractor'
          variable={watch('variable_name')}
          setVariableName={setVariableName}
        />
        <div className='h-[100px] w-full bg-white' />
        <Footer disabled={isMutating} onClose={closePanelHandler} isLoading={isMutating} />
      </form>

      <SlidePanel open={triggerPanelOpen} onClose={() => setTriggerPanelOpen(false)}>
        <SituationBuilder {...triggerComponentProps} />
      </SlidePanel>
      <AvoSlideCloseModal
        open={closeModalOpen}
        onContinue={() => setCloseModalOpen(false)}
        onDiscard={closePanel}
      />
    </div>
  );
};
